import {
	Inline,
	Alert,
	Box,
	Button,
	Heading,
	Icons,
	Stack,
	Col,
	Details,
	Divider,
	Grid,
	Spinner,
	Summary,
	DeferRender,
	Select,
	Text,
} from "@sembark-travel/ui/base"
import { ButtonLink, Link, queryToSearch } from "@sembark-travel/ui/router"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { Markdown } from "@sembark-travel/ui/markdown"
import { useXHR } from "@sembark-travel/xhr"
import {
	FieldArray,
	Form,
	FormApi,
	Field,
	arrayMutators,
	TextInputField,
	SelectField,
	withServerErrors,
	SubmissionError,
	TextAreaInputField,
	GetFieldValue,
	MarkdownInputField,
} from "@sembark-travel/ui/form"
import React, { useState } from "react"
import useSWR from "swr"
import { Omit } from "utility-types"
import config from "../config"
import { generatePath, useNavigate } from "../router-utils"
import { Cabs, DaywiseScheduleDay, Hotels } from "./ItineraryItem"
import type { ITripQuote } from "./store"
import type { ITermsAndCondition } from "./../TermsAndConditions"
import { SelectTermsAndConditions } from "./../TermsAndConditions"
import { useFetchQuote } from "./store"
import {
	dateToUTCString,
	formatDate,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import {
	SelectInclusionExclusionPreset,
	TInclusionExclusionPreset,
} from "../InclusionExclusionPresets"

// New data structure type for inclusions/exclusions with category binding
type InclusionExclusionItem = {
	value: string
	category: string | null
}

interface IDaywiseSchedulePayload {
	date: Date
	title: string
	description: string
	transport_service_id?: number
	travel_activity_id?: number
	travel_activity_ticket_type_id?: number
	options?: Array<
		Omit<IDaywiseSchedulePayload, "title"> & {
			name: string
		}
	>
}

interface IItineraryInitialValues {
	daywise_schedules?: Array<IDaywiseSchedulePayload>
	inclusions?: Array<InclusionExclusionItem>
	exclusions?: Array<InclusionExclusionItem>
	inclusion_exclusion_comment: string
	terms_and_conditions?: ITermsAndCondition
	inclusion_exclusion_preset?: TInclusionExclusionPreset
	other_info?: string
}

interface CreateItineraryProps {
	quote: ITripQuote
	onCancel?: () => void
	onSuccess?: (quote: ITripQuote) => void
}

type InclusionExclusionRow = {
	category?: string
	inclusion?: string | boolean
	exclusion?: string | boolean
	category_editable?: boolean
	is_boolean?: boolean
}

function InclusionExclusionSectionFormField({
	label,
	fieldName,
	form,
	draggedIndex,
	setDraggedIndex,
	dragOverIndex,
	setDragOverIndex,
	addButtonLabel,
	removeAllTitle,
	removeItemTitle,
	extraContent,
}: {
	label: string
	fieldName: "inclusions" | "exclusions"
	form: FormApi<IItineraryInitialValues>
	draggedIndex: number | null
	setDraggedIndex: (idx: number | null) => void
	dragOverIndex: number | null
	setDragOverIndex: (idx: number | null) => void
	addButtonLabel: string
	removeAllTitle: string
	removeItemTitle: string
	extraContent?: React.ReactNode
}) {
	const canDragRef = React.useRef(false)

	return (
		<Stack as="section">
			<Inline
				as="header"
				alignItems="center"
				justifyContent="between"
				paddingY="2"
				borderBottomWidth="1"
			>
				<Text fontWeight="semibold">{label}</Text>
				<FieldArray<InclusionExclusionItem> name={fieldName}>
					{({ fields }) =>
						fields.length ? (
							<Button
								title={removeAllTitle}
								inline
								onClick={() => form.change(fieldName, [])}
							>
								<Icons.Cancel color="danger" />
							</Button>
						) : null
					}
				</FieldArray>
			</Inline>
			<FieldArray<InclusionExclusionItem> name={fieldName}>
				{({ fields }) => (
					<Stack gap="1">
						{fields.map((name, index) => {
							return (
								<Field<InclusionExclusionItem> name={name} key={name}>
									{({ input }) => {
										const categoryLabel = input.value?.category || undefined
										return (
											<>
												{dragOverIndex === index &&
													draggedIndex !== null &&
													draggedIndex !== index &&
													draggedIndex > index && (
														<Divider
															style={{ borderColor: "#3b82f6" }}
															borderTopWidth="2"
															marginY="0"
															sm
														/>
													)}
												<Inline
													key={name}
													gap="2"
													alignItems="center"
													paddingY="2"
													draggable={
														typeof fields.length === "number" &&
														fields.length > 1
													}
													onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
														if (!canDragRef.current) {
															e.preventDefault()
															return
														}
														setDraggedIndex(index)
													}}
													onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
														e.preventDefault()
														setDragOverIndex(index)
													}}
													onDrop={(e: React.DragEvent<HTMLDivElement>) => {
														e.preventDefault()
														if (
															draggedIndex !== null &&
															draggedIndex !== index
														) {
															fields.move(draggedIndex, index)
														}
														setDraggedIndex(null)
														setDragOverIndex(null)
														canDragRef.current = false
													}}
													onDragEnd={() => {
														setDraggedIndex(null)
														setDragOverIndex(null)
														canDragRef.current = false
													}}
													onMouseUp={() => {
														canDragRef.current = false
													}}
												>
													{typeof fields.length === "number" &&
														fields.length > 1 && (
															<span
																style={{
																	cursor: "grab",
																	display: "flex",
																	alignItems: "center",
																}}
																onMouseDown={() => {
																	canDragRef.current = true
																}}
															>
																<Icons.GripVertical />
															</span>
														)}
													<Box flex="1">
														<TextInputField
															label={categoryLabel || undefined}
															name={`${name}.value`}
															type="text"
														/>
													</Box>
													<Button
														title={removeItemTitle}
														inline
														onClick={() => fields.remove(index)}
													>
														<Icons.Cancel />
													</Button>
												</Inline>
												{dragOverIndex === index &&
													draggedIndex !== null &&
													draggedIndex !== index &&
													draggedIndex < index && (
														<Divider
															style={{ borderColor: "#3b82f6" }}
															borderTopWidth="2"
															marginY="0"
															sm
														/>
													)}
											</>
										)
									}}
								</Field>
							)
						})}
						<Inline paddingY="2" paddingX="2">
							<Button
								size="sm"
								onClick={() => fields.push({ value: "", category: null })}
							>
								{addButtonLabel}
							</Button>
						</Inline>
						{extraContent}
					</Stack>
				)}
			</FieldArray>
		</Stack>
	)
}

export function CreateItinerary({
	quote: tripQuote,
	onCancel,
	onSuccess,
}: CreateItineraryProps) {
	const [draggedInclusionIndex, setDraggedInclusionIndex] = useState<
		number | null
	>(null)
	const [dragOverInclusionIndex, setDragOverInclusionIndex] = useState<
		number | null
	>(null)
	const [draggedExclusionIndex, setDraggedExclusionIndex] = useState<
		number | null
	>(null)
	const [dragOverExclusionIndex, setDragOverExclusionIndex] = useState<
		number | null
	>(null)

	const { id, quote, shared_by_tenant } = tripQuote
	const xhr = useXHR()
	const {
		data: initialValues,
		isValidating,
		error,
	} = useSWR<IItineraryInitialValues>(
		`/quote-itineraries/${tripQuote.id}/edit`,
		() =>
			xhr
				.get(`/quote-itineraries/${tripQuote.id}/edit`)
				.then((resp) => resp.data)
				.then((data) => {
					// Transform inclusion_exclusions into inclusions and exclusions
					const inclusions = data.inclusion_exclusions
						? data.inclusion_exclusions
								.filter(
									(row: InclusionExclusionRow) =>
										typeof row.inclusion === "string"
								)
								.map(
									(row: InclusionExclusionRow): InclusionExclusionItem => ({
										value: row.inclusion as string,
										category:
											row.category && row.category.trim() !== ""
												? row.category
												: null,
									})
								)
						: []

					const exclusions = data.inclusion_exclusions
						? data.inclusion_exclusions
								.filter(
									(row: InclusionExclusionRow) =>
										typeof row.exclusion === "string"
								)
								.map(
									(row: InclusionExclusionRow): InclusionExclusionItem => ({
										value: row.exclusion as string,
										category:
											row.category && row.category.trim() !== ""
												? row.category
												: null,
									})
								)
						: []

					return {
						...data,
						daywise_schedules: data.daywise_schedules.map(
							(
								schedule: Omit<IDaywiseSchedulePayload, "date"> & {
									date: string
									date_local?: string
								}
							) => ({
								...schedule,
								date: localOrUtcTimestampToLocalDate(
									schedule.date_local,
									schedule.date
								),
								description: schedule.description || "",
							})
						),
						inclusions,
						exclusions,
					}
				}),
		{ revalidateOnFocus: false }
	)

	// Memoize inclusion and exclusion arrays with category binding

	if (error && !initialValues) {
		return <Alert status="error">{error.message}</Alert>
	}
	if (!quote || !initialValues || isValidating) {
		return <Spinner padding="4" alignCenter />
	}
	const { hotels, cabs, start_date, start_date_local } = quote
	const startDateLocal = localOrUtcTimestampToLocalDate(
		start_date_local,
		start_date
	)

	return (
		<Form<IItineraryInitialValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				// Reconstruct inclusion_exclusions array format for API using new data structure
				const inclusion_exclusions = [
					...(values.inclusions || [])
						.filter((item: InclusionExclusionItem) => item && item.value)
						.map((item: InclusionExclusionItem) => ({
							category: item.category || undefined,
							is_boolean: 0,
							inclusion: item.value,
						})),
					...(values.exclusions || [])
						.filter((item: InclusionExclusionItem) => item && item.value)
						.map((item: InclusionExclusionItem) => ({
							category: item.category || undefined,
							is_boolean: 0,
							exclusion: item.value,
						})),
				]
				const data = {
					...values,
					inclusion_exclusion_preset: values.inclusion_exclusion_preset?.id,
					terms_and_conditions: values.terms_and_conditions?.id,
					timezone_offset: config.timezoneOffset,
					daywise_schedules: shared_by_tenant
						? undefined
						: values.daywise_schedules?.map(
								({ date, options, ...otherData }) => ({
									...otherData,
									date: dateToUTCString(date),
									date_local: formatDate(date, "YYYY-MM-DD"),
								})
							) || [],
					inclusion_exclusions,
				}
				// Remove inclusions and exclusions from payload
				delete data.inclusions
				delete data.exclusions
				const {
					data: { data: quote },
				} = await xhr.post(`/quote-itineraries/${id}`, data)
				onSuccess && onSuccess(quote)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					{shared_by_tenant ? (
						<Alert status="warning" title="Editting only Terms and Other Info">
							This package is received from <b>{shared_by_tenant.short_name}</b>
							. Hence, You can not update the Inclusions/Exclusions and Daywise
							Itinerary. If a change is required, please{" "}
							<b>request an update</b> from {shared_by_tenant.short_name}.
						</Alert>
					) : null}
					{!shared_by_tenant ? (
						<>
							<Details>
								<Summary>Hotels</Summary>
								{hotels.length ? (
									<Hotels quote={quote} />
								) : (
									<Box color="muted">No Hotels Provided</Box>
								)}
								<Box marginTop="2" color="muted">
									To edit hotels, please edit{" "}
									<Link
										to={
											generatePath("/trips/:tripId/new-quote", {
												tripId: tripQuote.trip_id.toString(),
											}) +
											queryToSearch({ quoteId: tripQuote.id }) +
											"#quote_hotels"
										}
									>
										quote services
									</Link>
								</Box>
							</Details>
							<Divider />
							<Details>
								<Summary>Cabs</Summary>
								{cabs.length ? (
									<Cabs quote={quote} />
								) : (
									<Box color="muted">No cabs Provided</Box>
								)}
								<Box marginTop="2" color="muted">
									To edit cabs, please edit{" "}
									<Link
										to={
											generatePath("/trips/:tripId/new-quote", {
												tripId: tripQuote.trip_id.toString(),
											}) +
											queryToSearch({ quoteId: tripQuote.id }) +
											"#quote_cabs"
										}
									>
										quote services
									</Link>
								</Box>
							</Details>
							<Divider />
							<Details open>
								<Summary>Inclusion/Exclusion</Summary>
								<Box marginTop="4">
									<SelectField
										name="inclusion_exclusion_preset"
										select={SelectInclusionExclusionPreset}
										fetchOnMount
										label="Select Inc/Exclusion Preset"
										onChange={(
											value: TInclusionExclusionPreset | undefined | null
										) => {
											form.change(
												"inclusion_exclusion_preset",
												value || undefined
											)
											if (value) {
												// Convert preset string arrays to new data structure
												const inclusions = (value.inclusions || []).map(
													(item: string): InclusionExclusionItem => ({
														value: item,
														category: null,
													})
												)
												const exclusions = (value.exclusions || []).map(
													(item: string): InclusionExclusionItem => ({
														value: item,
														category: null,
													})
												)
												form.change("inclusions", inclusions)
												form.change("exclusions", exclusions)
											} else {
												form.change("inclusions", [])
												form.change("exclusions", [])
											}
										}}
										marginBottom="3"
									/>
									<Grid gap="10">
										<Col sm={12} md={6}>
											<InclusionExclusionSectionFormField
												label="Included"
												fieldName="inclusions"
												form={form}
												draggedIndex={draggedInclusionIndex}
												setDraggedIndex={setDraggedInclusionIndex}
												dragOverIndex={dragOverInclusionIndex}
												setDragOverIndex={setDragOverInclusionIndex}
												addButtonLabel="Add Inclusion"
												removeAllTitle="Remove All"
												removeItemTitle="Remove Inclusion"
											/>
										</Col>
										<Col>
											<InclusionExclusionSectionFormField
												label="Excluded"
												fieldName="exclusions"
												form={form}
												draggedIndex={draggedExclusionIndex}
												setDraggedIndex={setDraggedExclusionIndex}
												dragOverIndex={dragOverExclusionIndex}
												setDragOverIndex={setDragOverExclusionIndex}
												addButtonLabel="Add Exclusion"
												removeAllTitle="Remove All"
												removeItemTitle="Remove Exclusion"
												extraContent={
													<Box
														color="warning"
														fontWeight="normal"
														paddingLeft="8"
													>
														{initialValues.inclusion_exclusion_comment}
													</Box>
												}
											/>
										</Col>
									</Grid>
								</Box>
							</Details>
							<Divider />
							<FieldArray<
								Required<IItineraryInitialValues>["daywise_schedules"][number]
							> name="daywise_schedules">
								{({ fields }) =>
									fields.length ? (
										<Details open>
											<Summary>Day-wise Schedule</Summary>
											<Box
												as="ol"
												marginTop="2"
												bgColor="default"
												rounded="lg"
												borderWidth="1"
												className="list"
											>
												{fields.map((name) => {
													return (
														<Box
															as="li"
															key={name}
															bgColor={{ hover: "subtle" }}
														>
															<Grid>
																<Col xs={12} sm="auto">
																	<GetFieldValue<
																		IDaywiseSchedulePayload["date"]
																	>
																		name={`${name}.date`}
																	>
																		{({ value: date }) => (
																			<Box
																				marginBottom="4"
																				marginTop="2"
																				style={{ maxWidth: "150px" }}
																			>
																				<DaywiseScheduleDay
																					date={date}
																					from={startDateLocal}
																				/>
																			</Box>
																		)}
																	</GetFieldValue>
																</Col>
																<Col>
																	<Grid>
																		<Col sm={12} md={5} lg={4}>
																			<GetFieldValue<
																				IDaywiseSchedulePayload["options"]
																			>
																				name={`${name}.options`}
																			>
																				{({ value: options }) =>
																					options && options.length > 1 ? (
																						<Box>
																							<GetFieldValue
																								name={`${name}.title`}
																							>
																								{({
																									value: title,
																									onChange,
																								}) => {
																									const selectedOptions = (
																										options || []
																									).filter(
																										(o) => o.name === title
																									)
																									const value =
																										selectedOptions.length
																											? selectedOptions[0]
																											: undefined
																									return (
																										<Select
																											label="Title"
																											options={options}
																											value={value}
																											placeholder="Title of the Day visit"
																											creatable
																											onChange={(value) => {
																												onChange(
																													value
																														? value.name
																														: ""
																												)
																												if (
																													value &&
																													value.description
																												) {
																													form.change(
																														`${name}.description` as never,
																														value.description as never
																													)
																												}
																											}}
																										/>
																									)
																								}}
																							</GetFieldValue>
																						</Box>
																					) : (
																						<TextInputField
																							label="Title"
																							name={`${name}.title`}
																							type="text"
																							placeholder="Title of the Day visit"
																						/>
																					)
																				}
																			</GetFieldValue>
																		</Col>
																		<Col sm={12} md>
																			<TextAreaInputField
																				label="Description"
																				name={`${name}.description`}
																				placeholder="Please provide detailed description of the day visit..."
																				rows={8}
																			/>
																		</Col>
																	</Grid>
																</Col>
															</Grid>
														</Box>
													)
												})}
											</Box>
										</Details>
									) : (
										"No Day-wise Schedule provided"
									)
								}
							</FieldArray>
							<Divider />
						</>
					) : null}
					<Details initialOpen={Boolean(initialValues.other_info)}>
						<Summary>Important Notes</Summary>
						<Box marginTop="4">
							<MarkdownInputField
								label="Important Notes"
								name="other_info"
								placeholder="Any other information like contact Details or guidelines"
								required
								rows={10}
							/>
						</Box>
					</Details>
					<Divider />
					<Details open>
						<Summary>Terms and Conditions</Summary>
						<Stack gap="4" marginTop="4">
							<SelectField
								fetchOnMount
								select={SelectTermsAndConditions}
								name="terms_and_conditions"
								placeholder="Applied terms and conditions for this package"
							/>
							<GetFieldValue<
								IItineraryInitialValues["terms_and_conditions"]
							> name="terms_and_conditions">
								{({ value: terms_and_conditions }) =>
									terms_and_conditions ? (
										<Stack gap="2">
											{terms_and_conditions.deleted_at ? (
												<Alert
													status="warning"
													title="These terms have been archived"
												>
													Please select a diffent terms to align better with
													company policies.
												</Alert>
											) : null}
											<Box
												padding="4"
												borderWidth="1"
												rounded="md"
												style={{ maxHeight: "200px" }}
												overflow="auto"
												bgColor="subtle"
											>
												<DeferRender>
													<Markdown>
														{terms_and_conditions.description}
													</Markdown>
												</DeferRender>
											</Box>
										</Stack>
									) : null
								}
							</GetFieldValue>
						</Stack>
					</Details>
					<Divider />
					<Stack as="footer" gap="4">
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Please wait..." : "Save Details"}
							</Button>
							{onCancel ? (
								<Button onClick={onCancel} disabled={submitting}>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function EditOrCreateItinerary({
	quoteId,
	...props
}: Omit<CreateItineraryProps, "quote"> & { quoteId: string | number }) {
	const navigate = useNavigate()
	const { data: tripQuote, error, mutate } = useFetchQuote(quoteId)
	const back = React.useCallback(() => {
		if (tripQuote) {
			navigate(`/trips/:tripId/quotes/:quoteId`, {
				params: {
					tripId: String(tripQuote.trip_id),
					quoteId: String(tripQuote.id),
				},
				replace: true,
			})
		}
	}, [navigate, tripQuote])
	if (!tripQuote) {
		return <Spinner padding="4" alignCenter />
	}
	const { itinerary } = tripQuote
	return (
		<Stack gap="4">
			<Inline
				gap="4"
				alignItems="center"
				borderBottomWidth="1"
				paddingBottom="4"
			>
				<ButtonLink
					to={generatePath("/trips/:tripId/quotes/:quoteId", {
						tripId: String(tripQuote.trip_id),
						quoteId: String(tripQuote.id),
					})}
				>
					<Icons.ArrowLeft />
				</ButtonLink>
				<Heading>{itinerary ? "Edit Itinerary" : "Create Itinerary"}</Heading>
			</Inline>
			{error ? (
				<Alert status="error">{error?.message || "Something went wrong"}</Alert>
			) : null}
			<CreateItinerary
				{...props}
				quote={tripQuote}
				onCancel={back}
				onSuccess={() => {
					back()
					showSnackbar("Itinerary created")
					mutate()
				}}
			/>
		</Stack>
	)
}
